import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import type { WeekKey } from "../utils/week-utils";

export type SubmittedWeeklyGroup =
  RouterOutputs["timeEntry"]["getSubmittedTimeEntries"][number];

export type SubmittedShift = SubmittedWeeklyGroup["shifts"][number];

export type SubmittedTimeEntry = SubmittedShift["timeEntries"][number];

/**
 * Hook to fetch submitted time entries for the current provider.
 * Returns entries grouped by week with pending/approved status.
 *
 * @param status - Filter by status: "pending", "approved", or "all"
 * @param weekKey - Optional week filter (e.g., "2025-W01") for approved/history entries
 */
export function useSubmittedTimeEntries(
  status?: "pending" | "approved" | "all",
  weekKey?: WeekKey,
) {
  const trpc = useTRPC();

  const {
    data: weeklyGroups,
    isLoading,
    error,
    refetch,
  } = useQuery(
    trpc.timeEntry.getSubmittedTimeEntries.queryOptions({
      status: status ?? "all",
      weekKey,
    }),
  );

  // Calculate totals
  const totalPending =
    weeklyGroups?.reduce((sum, g) => sum + g.pendingCount, 0) ?? 0;
  const totalApproved =
    weeklyGroups?.reduce((sum, g) => sum + g.approvedCount, 0) ?? 0;

  return {
    weeklyGroups: weeklyGroups ?? [],
    totalPending,
    totalApproved,
    isLoading,
    error,
    refetch,
  };
}
